import React, { useState, useEffect, useCallback, useRef } from "react";
import { ThemeLightState } from "./ThemeLightState";
import { useNavigate } from "react-router-dom";
import { fetchWalletSummary, shouldRefreshWalletData } from "../services/walletService";
import { ConnectButton } from "./ConnectButton";

// 导入图片（注意：实际使用时需要确保这些图片文件存在）
import bgMain from "../images/bg-main.png";
import rectangle4102 from "../images/rectangle-410-1.png";
import rectangle4103 from "../images/rectangle-410-2.png";
import rectangle4104 from "../images/rectangle-410-3.png";
import rectangle4105 from "../images/rectangle-410-4.png";
import rectangle4106 from "../images/rectangle-410-5.png";
import rectangle4107 from "../images/rectangle-410-6.png";
import rectangle410 from "../images/rectangle-410.png";
import rectangle411 from "../images/rectangle-411.png";
import memeCardCNImage from "../images/meme_card_cn.png";
import chineseIcon from "../images/chinese.png";

export const Home = () => {
  const [bscAddress, setBscAddress] = useState('');
  const [isDataLoading, setIsDataLoading] = useState(false);
  const connectButtonRef = useRef(null);

  // 获取钱包数据
  const getWalletData = useCallback(async (address) => {
    if (!address) return;
    
    try {
      setIsDataLoading(true);
      console.log('正在获取钱包数据...');
      
      // 检查是否需要刷新数据
      if (shouldRefreshWalletData(address)) {
        console.log('需要刷新钱包数据');
        await fetchWalletSummary(address);
      } else {
        console.log('使用缓存的钱包数据');
      }
    } catch (error) {
      console.error('获取钱包数据失败:', error);
    } finally {
      setIsDataLoading(false);
    }
  }, []);

  // 从localStorage加载BSC地址并获取钱包数据
  useEffect(() => {
    const savedAddress = localStorage.getItem('bscAddress');
    if (savedAddress) {
      setBscAddress(savedAddress);
      getWalletData(savedAddress);
    }
  }, [getWalletData]);

  const handleAddressChange = (address) => {
    if (address) {
      console.log('Wallet connected with address:', address);
      setBscAddress(address);
      localStorage.setItem('bscAddress', address);
      getWalletData(address);
    } else {
      console.log('Wallet disconnected');
      setBscAddress('');
      localStorage.removeItem('bscAddress');
    }
  };

  const navigate = useNavigate();

  // 通用的钱包连接检查函数
  const checkWalletConnection = (callback) => {
    if (!bscAddress) {
      // 如果connectButtonRef有效，调用其打开模态框的方法
      if (connectButtonRef.current) {
        connectButtonRef.current.openModal();
      } else {
        alert("Please connect your wallet first");
      }
      return false;
    }
    callback();
    return true;
  };

  const handleHoroscopeClick = () => {
    checkWalletConnection(() => navigate("/horoscope"));
  };

  const handleMemeTagClick = (version = 'en') => {
    checkWalletConnection(() => navigate(`/memetag_${version}`));
  };

  const handleMBTIClick = () => {
    checkWalletConnection(() => navigate("/mbti"));
  };

  const handleDeepResearchClick = () => {
    navigate("/deep-research");
  };

  const handleBinanceIDOClick = () => {
    checkWalletConnection(() => {
      // 如果已有BSC地址，可以直接处理相应卡片的逻辑
      console.log(`处理Binance IDO Analysis卡片的逻辑，使用地址: ${bscAddress}`);
      navigate("/binance-ido");
    });
  };

  const handleAIEventTrackerClick = () => {
    checkWalletConnection(() => navigate("/ai-event-tracker"));
  };

  const handleSmartYieldMoveClick = () => {
    checkWalletConnection(() => navigate("/smart-yield-move"));
  };

  return (
    <div className="w-full min-h-screen bg-element-lightwhite overflow-y-auto">
      <div className="relative w-full pb-20">
        {/* Background image */}
        <div className="fixed inset-0 z-0 overflow-hidden">
          <img
            className="w-full h-full object-cover"
            alt="Bg main"
            src={bgMain}
          />
        </div>

        {/* Content container */}
        <div className="relative z-10 pt-2 pb-20 flex flex-col items-center">
          {/* Header with logo and connect wallet button */}
          <div className="sticky top-0 left-0 right-0 z-20 flex justify-between items-center w-full px-4 py-3">
            {/* Logo on the left */}
            <div className="flex items-center">
              <img
                className="w-[120px] h-[42.631px] flex-shrink-0 aspect-ratio-[100.00/35.53] object-contain"
                alt="PIN AI Logo"
                src={rectangle411}
              />
            </div>

            {/* Connect wallet button */}
            <div className="absolute top-4 right-4 z-10 w-40">
              <ConnectButton
                onAddressChange={handleAddressChange}
                ref={connectButtonRef}
              />
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl font-bold text-[#1c1f25]">PIN AI Web App</h1>
          <p className="text-[#676C77]">Your Web3 Personal AI</p>

          {/* Cards container wrapper */}
          <div className="w-[92%] mx-auto mt-2 clearfix">
            {/* Cards container - Left Column */}
            <div className="flex flex-col w-[calc(50%-4px)] float-left gap-[8px]">
              {/* Card 3 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleDeepResearchClick()}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4103} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Token Deep Research – Unlock Honest Score
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Audit Binance token reports via on-chain data, get Honest Score report
                  </p>
                </div>
              </div>

              {/* Card 2 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleMemeTagClick("en")}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4102} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Memecoin Tag
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Discover your unique meme coin trading style
                  </p>
                </div>
              </div>

              {/* Card 5 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={handleMBTIClick}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4105} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Web3 Trading MBTI
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Meet your AI-generated MBTI persona—crafted from your wallet
                    activity. Discover who you are in the Web3 world.
                  </p>
                </div>
              </div>

              {/* Card 8 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleSmartYieldMoveClick()}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4107} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Smart Yield Move
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Stake your idle assets! Move them to Earn or on-chain
                    products to generate yield.
                  </p>

                  <ThemeLightState
                    className="self-stretch h-10 w-full mt-2"
                    divClassName="mt-[-4.50px] text-base mb-[-2.50px] whitespace-nowrap font-medium"
                    text="Coming"
                  />
                </div>
              </div>
            </div>

            {/* Cards container - Right Column */}
            <div className="flex flex-col w-[calc(50%-4px)] float-right gap-[8px]">
        
              {/* Card 1 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleBinanceIDOClick()}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle410} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Binance IDO Analysis
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Track your performance in Binance IDOs
                  </p>
                </div>
              </div>

              {/* Card 4 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={handleHoroscopeClick}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4104} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Horoscope
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Your crypto fortune-telling, powered by DeepSeek
                  </p>
                </div>
              </div>

              {/* Card 6 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleMemeTagClick("cn")}
              >
                <div className="w-full">
                  <img
                    className="w-full"
                    alt="Rectangle"
                    src={memeCardCNImage}
                  />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    Spicy Takes on Meme
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    <img src={chineseIcon} alt="Chinese" className="w-6 h-6 inline-block mr-0.5 mb-0.5" />
                    Are you a meme coin killer or just someone left holding the bag?
                  </p>
                </div>
              </div>

              {/* Card 7 */}
              <div
                className="bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] mb-[8px]"
                onClick={() => handleAIEventTrackerClick()}
              >
                <div className="w-full">
                  <img className="w-full" alt="Rectangle" src={rectangle4106} />
                </div>

                <div className="p-[10px] pb-[12px]">
                  <div className="font-medium text-[#1c1f25] text-[16px] tracking-[0] leading-[1.25em]">
                    AI Event Tracker
                  </div>

                  <p className="font-normal text-[#676C77] text-[14px] tracking-[0] leading-[1.25em] mt-[6px]">
                    Automatically adds events about your held tokens to your
                    calendar
                  </p>

                  <ThemeLightState
                    className="self-stretch h-10 w-full mt-2"
                    divClassName="text-base whitespace-nowrap font-medium"
                    text="Coming"
                  />
                </div>
              </div>
            </div>

            {/* Clear float */}
            <div className="clear-both"></div>
          </div>
        </div>
      </div>
    </div>
  );
};
